import React, { useCallback, useDeferredValue, useMemo, useState } from "react";
import { useSelector } from "@legendapp/state/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, LogIn, RefreshCw, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  useGoogleConnection,
  isGoogleAuthError,
} from "@/contexts/GoogleConnectionContext";
import { getStoredToken } from "@/lib/googleAuth";
import { useGoogleDriveFolderContents } from "@/hooks/googleDrive/useGoogleDriveFolderContents";
import { useGoogleDriveSyncMap } from "@/hooks/googleDrive/useGoogleDriveSyncMap";
import { useGoogleDriveImportMutations } from "@/hooks/googleDrive/useGoogleDriveImportMutations";
import {
  googleDriveBrowser$,
  navigateToBreadcrumb,
  navigateToFolder,
  toggleFileSelection,
  clearFileSelection,
  setSearchQuery,
} from "@/state/googleDriveBrowserLegend";
import {
  listFolderChildren,
  searchDriveFiles,
  collectFilesFromFolders,
  DriveAuthError,
  type DriveEntry,
  type DriveImportCandidate,
} from "@/services/googleDriveService";
import type { DriveImportFile } from "@/services/googleDriveImportService";
import { getThoughtProcessPreferences } from "@/lib/thoughtProcessPreferences";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { GoogleConnectCard } from "@/components/google/GoogleConnectCard";
import { SiGoogledrive } from "react-icons/si";
import { GoogleDriveBreadcrumbs } from "./GoogleDriveBreadcrumbs";
import {
  GoogleDriveImportOptions,
  type GoogleDriveImportOptionsState,
} from "./GoogleDriveImportOptions";
import { GoogleDriveImportBar } from "./GoogleDriveImportBar";
import { GoogleDriveVirtualList } from "./GoogleDriveVirtualList";
import { GoogleDriveFolderRow } from "./GoogleDriveFolderRow";
import { GoogleDriveFileRow } from "./GoogleDriveFileRow";

type ListEntry = DriveEntry & { kind: "folder" | "file" };

type GoogleDriveBrowserProps = {
  workspaceId?: string | null;
  targetFolderId?: string | null;
  spaceId?: string | null;
};

function GoogleDriveBrowserInner({
  workspaceId: workspaceIdProp = null,
  targetFolderId = null,
  spaceId = null,
}: GoogleDriveBrowserProps) {
  const { currentWorkspaceId } = useWorkspace();
  const workspaceId = workspaceIdProp ?? currentWorkspaceId ?? null;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    connection,
    connectDrive,
    disconnect,
    refreshToken,
    applySession,
    handleAuthFailure,
  } = useGoogleConnection();
  const { importMutation, updateMutation } = useGoogleDriveImportMutations();

  const currentFolderId = useSelector(() =>
    googleDriveBrowser$.currentFolderId.get()
  );
  const breadcrumbs = useSelector(() => googleDriveBrowser$.breadcrumbs.get());
  const selectedFileIds = useSelector(() =>
    googleDriveBrowser$.selectedFileIds.get()
  );
  const searchQuery = useSelector(() => googleDriveBrowser$.searchQuery.get());
  const deferredSearch = useDeferredValue(searchQuery.trim());

  const [importOptions, setImportOptions] =
    useState<GoogleDriveImportOptionsState>(() => ({
      mirrorFolders: true,
      includeInRag: true,
      embedAfterImport: getThoughtProcessPreferences().autoEmbedDocuments,
    }));
  const [progress, setProgress] = useState<{
    current: string;
    completed: number;
    total: number;
  } | null>(null);
  const [isScanning, setIsScanning] = useState(false);

  const folderQuery = useGoogleDriveFolderContents(currentFolderId);

  const searchQueryResult = useQuery({
    queryKey: ["google-drive", "search", deferredSearch, connection.token],
    queryFn: async () => {
      try {
        const token = connection.token ?? getStoredToken();
        if (!token) throw new DriveAuthError();
        const result = await searchDriveFiles(deferredSearch, token);
        const latestToken = getStoredToken();
        if (latestToken && latestToken !== connection.token) {
          applySession(latestToken);
        }
        return result;
      } catch (error) {
        if (isGoogleAuthError(error)) {
          handleAuthFailure();
        }
        throw error;
      }
    },
    enabled:
      connection.driveConnected && !!connection.token && deferredSearch.length >= 2,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (isGoogleAuthError(error)) return false;
      return failureCount < 1;
    },
  });

  const drivePathPrefix = useMemo(
    () => breadcrumbs.slice(1).map((b) => b.name).join("/"),
    [breadcrumbs]
  );

  const { folders, files } = useMemo(() => {
    if (deferredSearch.length >= 2 && searchQueryResult.data) {
      const searchFolders = searchQueryResult.data.filter((e) => e.isFolder);
      const searchFiles = searchQueryResult.data.filter((e) => !e.isFolder);
      return { folders: searchFolders, files: searchFiles };
    }
    return {
      folders: folderQuery.data?.folders ?? [],
      files: folderQuery.data?.files ?? [],
    };
  }, [deferredSearch, searchQueryResult.data, folderQuery.data]);

  const syncMap = useGoogleDriveSyncMap(files);

  const listEntries = useMemo((): ListEntry[] => {
    const folderEntries: ListEntry[] = folders.map((f) => ({
      ...f,
      kind: "folder" as const,
    }));
    const fileEntries: ListEntry[] = files.map((f) => ({
      ...f,
      kind: "file" as const,
    }));
    return [...folderEntries, ...fileEntries];
  }, [folders, files]);

  const selectedIds = useMemo(
    () => Object.keys(selectedFileIds),
    [selectedFileIds]
  );

  const selectedFiles = useMemo(
    () => files.filter((f) => selectedFileIds[f.id]),
    [files, selectedFileIds]
  );

  const staleFiles = useMemo(
    () => files.filter((f) => syncMap.get(f.id)?.status === "stale"),
    [files, syncMap]
  );

  const activeError = useMemo(() => {
    if (deferredSearch.length >= 2 && searchQueryResult.isError) {
      return searchQueryResult.error;
    }
    if (folderQuery.isError) {
      return folderQuery.error;
    }
    return null;
  }, [
    deferredSearch.length,
    searchQueryResult.isError,
    searchQueryResult.error,
    folderQuery.isError,
    folderQuery.error,
  ]);

  const isAuthError = isGoogleAuthError(activeError);

  const handleOpenFolder = useCallback((id: string, name: string) => {
    setSearchQuery("");
    navigateToFolder(id, name);
  }, []);

  const handleBreadcrumb = useCallback((index: number) => {
    setSearchQuery("");
    navigateToBreadcrumb(index);
  }, []);

  const handleToggleFile = useCallback((id: string) => {
    toggleFileSelection(id);
  }, []);

  const prefetchFolder = useCallback(
    (folderId: string) => {
      const token = connection.token ?? getStoredToken();
      if (!token) return;
      queryClient.prefetchQuery({
        queryKey: ["google-drive", "folder", folderId, token],
        queryFn: () => listFolderChildren(folderId, token),
        staleTime: 60_000,
      });
    },
    [connection.token, queryClient]
  );

  const toImportFiles = useCallback(
    (entries: (DriveEntry & { drivePath?: string })[]): DriveImportFile[] =>
      entries.map((f) => ({
        ...f,
        drivePath: f.drivePath ?? (drivePathPrefix || undefined),
      })),
    [drivePathPrefix]
  );

  const collectBulkImportFiles = useCallback(
    async (
      mode: "root-files" | "folders" | "all"
    ): Promise<DriveImportCandidate[]> => {
      const token = connection.token ?? getStoredToken();
      if (!token) return [];

      const rootFiles: DriveImportCandidate[] = files.map((f) => ({
        ...f,
        drivePath: drivePathPrefix || undefined,
      }));

      if (mode === "root-files") {
        return rootFiles;
      }

      setIsScanning(true);
      setProgress({ current: "Scanning folders…", completed: 0, total: 0 });

      try {
        const folderFiles = await collectFilesFromFolders(
          folders,
          token,
          drivePathPrefix,
          (message) => {
            setProgress({ current: message, completed: 0, total: 0 });
          }
        );

        if (mode === "folders") {
          return folderFiles;
        }

        const seen = new Set<string>();
        const combined: DriveImportCandidate[] = [];
        for (const file of [...rootFiles, ...folderFiles]) {
          if (seen.has(file.id)) continue;
          seen.add(file.id);
          combined.push(file);
        }
        return combined;
      } finally {
        setIsScanning(false);
      }
    },
    [connection.token, files, folders, drivePathPrefix]
  );

  const runImport = useCallback(
    async (
      entries: (DriveEntry & { drivePath?: string })[],
      updateExisting: boolean
    ) => {
      const token = connection.token ?? getStoredToken();
      if (!token || entries.length === 0) return;

      setProgress({ current: "Starting…", completed: 0, total: entries.length });

      const mutation = updateExisting ? updateMutation : importMutation;

      try {
        const result = await mutation.mutateAsync({
          files: toImportFiles(entries),
          options: {
            token,
            workspaceId,
            spaceId,
            targetFolderId,
            mirrorFolders: importOptions.mirrorFolders,
            includeInRag: importOptions.includeInRag,
            embedAfterImport: importOptions.embedAfterImport,
            updateExisting,
            onProgress: (current, completed, total) => {
              setProgress({ current, completed, total });
            },
          },
        });

        const ok = result.created.length + result.updated.length;
        toast({
          title: updateExisting ? "Documents updated" : "Import complete",
          description: `${ok} succeeded${result.failed.length ? `, ${result.failed.length} failed` : ""}.`,
          variant:
            result.failed.length && ok === 0 ? "destructive" : "default",
        });
        clearFileSelection();
      } catch (e) {
        toast({
          title: "Import failed",
          description: e instanceof Error ? e.message : "Unknown error",
          variant: "destructive",
        });
      } finally {
        setTimeout(() => setProgress(null), 1500);
      }
    },
    [
      connection.token,
      importMutation,
      updateMutation,
      toImportFiles,
      workspaceId,
      spaceId,
      targetFolderId,
      importOptions,
      toast,
    ]
  );

  const handleImport = useCallback(() => {
    void runImport(selectedFiles, false);
  }, [runImport, selectedFiles]);

  const runBulkImport = useCallback(
    async (mode: "root-files" | "folders" | "all") => {
      try {
        const entries = await collectBulkImportFiles(mode);
        if (entries.length === 0) {
          const labels = {
            "root-files": "No files in this folder to import.",
            folders: "No importable files found inside folders.",
            all: "No importable files found.",
          };
          toast({
            title: "Nothing to import",
            description: labels[mode],
            variant: "default",
          });
          return;
        }
        await runImport(entries, false);
      } catch (e) {
        if (isGoogleAuthError(e)) {
          handleAuthFailure();
        }
        toast({
          title: "Scan failed",
          description:
            e instanceof Error ? e.message : "Could not scan Drive folders",
          variant: "destructive",
        });
      }
    },
    [collectBulkImportFiles, runImport, toast, handleAuthFailure]
  );

  const handleImportRootFiles = useCallback(() => {
    void runBulkImport("root-files");
  }, [runBulkImport]);

  const handleImportFolders = useCallback(() => {
    void runBulkImport("folders");
  }, [runBulkImport]);

  const handleImportAll = useCallback(() => {
    void runBulkImport("all");
  }, [runBulkImport]);

  const handleUpdateStale = useCallback(() => {
    void runImport(staleFiles, true);
  }, [runImport, staleFiles]);

  const handleRefresh = useCallback(async () => {
    const ok = await refreshToken();
    if (!ok) {
      toast({
        title: "Session expired",
        description: "Please sign in to Google Drive again.",
        variant: "destructive",
      });
      return;
    }
    if (deferredSearch.length >= 2) {
      await searchQueryResult.refetch();
    } else {
      await folderQuery.refetch();
    }
    toast({ title: "Refreshed", description: "Checked Drive file versions." });
  }, [refreshToken, deferredSearch, searchQueryResult, folderQuery, toast]);

  const handleRetry = useCallback(() => {
    if (deferredSearch.length >= 2) {
      void searchQueryResult.refetch();
    } else {
      void folderQuery.refetch();
    }
  }, [deferredSearch, searchQueryResult, folderQuery]);

  const renderRow = useCallback(
    (entry: ListEntry) => {
      if (entry.kind === "folder") {
        return (
          <GoogleDriveFolderRow
            id={entry.id}
            name={entry.name}
            onOpen={handleOpenFolder}
            onHover={prefetchFolder}
          />
        );
      }
      const sync = syncMap.get(entry.id);
      return (
        <GoogleDriveFileRow
          id={entry.id}
          name={entry.name}
          modifiedTime={entry.modifiedTime}
          selected={!!selectedFileIds[entry.id]}
          syncStatus={sync?.status ?? "new"}
          onToggle={handleToggleFile}
        />
      );
    },
    [
      handleOpenFolder,
      prefetchFolder,
      syncMap,
      selectedFileIds,
      handleToggleFile,
    ]
  );

  if (!connection.driveConnected) {
    return (
      <GoogleConnectCard
        service="drive"
        connection={connection}
        onConnect={connectDrive}
        onDisconnect={disconnect}
        icon={<SiGoogledrive className="h-5 w-5 text-[#4285F4]" />}
      />
    );
  }

  const isLoading =
    (deferredSearch.length >= 2
      ? searchQueryResult.isLoading
      : folderQuery.isLoading) && listEntries.length === 0;

  return (
    <div className="flex flex-col gap-4">
      <GoogleConnectCard
        service="drive"
        connection={connection}
        onConnect={connectDrive}
        onDisconnect={disconnect}
        icon={<SiGoogledrive className="h-5 w-5 text-[#4285F4]" />}
      />

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search Drive files…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {deferredSearch.length < 2 && (
        <GoogleDriveBreadcrumbs
          breadcrumbs={breadcrumbs}
          onNavigate={handleBreadcrumb}
        />
      )}

      <GoogleDriveImportOptions
        options={importOptions}
        onChange={setImportOptions}
      />

      <GoogleDriveImportBar
        selectedCount={selectedIds.length}
        staleCount={staleFiles.length}
        rootFileCount={deferredSearch.length < 2 ? files.length : 0}
        folderCount={deferredSearch.length < 2 ? folders.length : 0}
        isImporting={importMutation.isPending || updateMutation.isPending}
        isScanning={isScanning}
        progress={progress}
        onImport={handleImport}
        onImportRootFiles={handleImportRootFiles}
        onImportFolders={handleImportFolders}
        onImportAll={handleImportAll}
        onUpdateStale={handleUpdateStale}
        onRefresh={handleRefresh}
        onClearSelection={clearFileSelection}
      />

      {activeError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <p className="text-destructive">
            {activeError instanceof Error
              ? activeError.message
              : "Failed to load folder"}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {isAuthError ? (
              <Button size="sm" variant="default" onClick={connectDrive}>
                <LogIn className="mr-2 h-4 w-4" />
                Sign in again
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={handleRetry}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading…
        </div>
      ) : !activeError && listEntries.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No text or data files in this folder.
        </p>
      ) : !activeError ? (
        <GoogleDriveVirtualList items={listEntries} renderItem={renderRow} />
      ) : null}
    </div>
  );
}

export const GoogleDriveBrowser = React.memo(function GoogleDriveBrowser(
  props: GoogleDriveBrowserProps
) {
  return <GoogleDriveBrowserInner {...props} />;
});
